import React from "react";
import { Badge, Card, CardBody, Table } from "reactstrap";
import {
  WorkOrderItemTypeLabelMap,
  type WorkOrderItemTypeValue,
} from "features/WorkOrders/constants/work-order-item.constants";
import { formatCurrency } from "utils/currency";
import { useWorkOrderOverview } from "../WorkOrderOverviewContext";

export default function LineItems() {
  const { workOrder } = useWorkOrderOverview();

  if (!workOrder) {
    return null;
  }

  const items = workOrder.items ?? [];
  const total = items.reduce((sum, l) => sum + Number(l.amount ?? 0), 0);

  return (
    <Card>
      <CardBody>
        <div className="d-flex align-items-center justify-content-between mb-4">
          <h6 className="text-uppercase mb-0">Line items (BOQ)</h6>
          {items.length > 0 && (
            <span className="text-muted small">
              Total:{" "}
              <span className="fw-semibold text-body">
                {formatCurrency(total)}
              </span>
            </span>
          )}
        </div>

        {items.length > 0 ? (
          <div className="table-responsive table-card">
            <Table className="table-bordered table-nowrap mb-0 align-middle">
              <thead className="table-light text-muted small">
                <tr>
                  <th>#</th>
                  <th>Type</th>
                  <th>Product / Description</th>
                  <th>Unit</th>
                  <th className="text-end">Rate</th>
                  <th className="text-end">Qty</th>
                  <th className="text-end">Amount</th>
                </tr>
              </thead>
              <tbody>
                {items.map((line, i) => {
                  const amount = Number(line.amount ?? 0);
                  const isExtra = line.isExtra;
                  const isModified = line.isModified;
                  return (
                    <tr key={line.id ?? i}>
                      <td className="text-muted">{i + 1}</td>
                      <td>
                        <span className="small">
                          {line.type
                            ? (WorkOrderItemTypeLabelMap[
                                line.type as WorkOrderItemTypeValue
                              ] ?? line.type)
                            : "—"}
                        </span>
                      </td>
                      <td style={{ whiteSpace: "pre-wrap", maxWidth: 280 }}>
                        <span>
                          {line.product?.name?.trim()
                            ? line.product.name
                            : (line.description?.trim() ?? "—")}
                        </span>
                        {isExtra && (
                          <Badge color="warning" className="ms-2 fs-10" pill>
                            Extra
                          </Badge>
                        )}
                        {isModified && (
                          <Badge color="info" className="ms-2 fs-10" pill>
                            Modified
                          </Badge>
                        )}
                      </td>
                      <td className="text-muted small">{line.unit ?? "—"}</td>
                      <td className="text-end tabular-nums">
                        {formatCurrency(Number(line.rate ?? 0))}
                      </td>
                      <td className="text-end tabular-nums">
                        {Number(line.quantity ?? 0)}
                      </td>
                      <td className="text-end tabular-nums fw-semibold">
                        {formatCurrency(amount)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="table-light">
                <tr>
                  <td colSpan={6} className="text-end fw-semibold small text-muted">
                    Total
                  </td>
                  <td className="text-end fw-bold tabular-nums">
                    {formatCurrency(total)}
                  </td>
                </tr>
              </tfoot>
            </Table>
          </div>
        ) : (
          <p className="text-muted mb-0">No line items.</p>
        )}
      </CardBody>
    </Card>
  );
}
